import React, { Component } from 'react';
import { Flex, Button, TextField, Dropdown, RadioButton } from 'monday-ui-react-core';
import { Heading } from "monday-ui-react-core/next";
import classes from "./backup-editor.module.scss";
import { showNotice } from '../../services/error-handling';
import { getBoardGroups, searchBoardsByName } from '../../services/api-service';
import mondaySdk from "monday-sdk-js";

class BackupEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      workspaceId: props.workspaceId,
      backupType: null,
      backupName: "",
      backupVisibility: "private",
      backupDescription: "",
      itemId: null,
      itemName: null,
      itemKind: "",
      isItemsDropdownsLoading: false,
      boardGroupId: null,
      boardGroupName: null,
      isBoardGroupDropdownsLoading: false,
      itemOptions: [],
      boardGroupOptions: [],
      onCloseClick: props.onCloseClick,
      onCreateBackupClick: props.onCreateBackupClick,
      userId: null,
      loadingOnCreation: false
    };
  }

  componentDidMount() {
    const monday = mondaySdk();

    monday.listen("context", (res) => {
      this.setState({
        isAdmin: res.data.user.isAdmin,
        isGuest: res.data.user.isGuest,
        isViewOnly: res.data.user.isViewOnly,
        userId: res.data.user.id,
        workspaceId: res.data.workspaceId,
        timeZoneOffset: res.data.user.timeZoneOffset
      });
    });
  }

  async save() {
    if((this.state.backupType !== "doc") && (this.state.backupType !== "board") && (this.state.backupType !== "group")) {
      showNotice("error", "Please select a snapshot type");
      return;
    }

    if(this.state.itemId === null) {
      showNotice("error", "Please select an object to backup from the dropdown");
      return;
    }

    if((this.state.backupType === "group") && (this.state.boardGroupId === null)) {
      showNotice("error", "Please select a group from the dropdown");
      return;
    }

    if(this.validateBackupName(this.state.backupName).status === "error") {
      showNotice("error", "Please correct the validation errors");
      return;
    }

    this.setState({ 
      loadingOnCreation: true
    });

    const backupData = {
      boardId: this.state.itemId,
      backupName: this.state.backupName,
      backupType: this.state.backupType,
      workspaceId: this.state.workspaceId,
      userId: this.state.userId,
      backupVisibility: this.state.backupVisibility,
      itemName: (this.state.backupType === "group") ? (this.state.boardGroupName + " [" + this.state.itemName + "]") : this.state.itemName,
      metadata: { 
        description: this.state.backupDescription,
        backupBoardGroupId: this.state.boardGroupId
      }
    }

    await this.state.onCreateBackupClick(backupData);

    this.setState({ 
      loadingOnCreation: false
    });
  }

  validateBackupName(name) {
    if(!name || (name.length < 5)) {
      return {
        status: "error",
        text: "Snapshot name must be at least 5 characters"
      };
    }

    return {};
  }

  async fetchItems(searchStr) {
    const data = await searchBoardsByName(this.state.workspaceId, "board", searchStr);
    let results = [];
    data.forEach((board) => {
      results.push({
        label: board.name,
        value: board.id,
        board_kind: board.board_kind
      });
    })

    this.setState({
      itemOptions: results,
      isItemsDropdownsLoading: false,
      isBoardGroupDropdownsLoading: false
    });
  }

  async fetchGroups(boardId) {
    const data = await getBoardGroups(boardId);
    let results = [];
    data.groups.forEach((group) => {
      results.push({
        label: group.title,
        value: group.id
      })
    })

    this.setState({
      boardGroupOptions: results,
      isItemsDropdownsLoading: false,
      isBoardGroupDropdownsLoading: false
    });
  }

  async fetchDocs(searchStr) {
    const data = await searchBoardsByName(this.state.workspaceId, "document", searchStr);
    let results = [];
    data.forEach((doc) => {
      results.push({
        label: doc.name,
        value: doc.id,
        board_kind: doc.board_kind
      })
    })

    this.setState({
      itemOptions: results,
      isItemsDropdownsLoading: false,
      isBoardGroupDropdownsLoading: false
    });
  }

  generateBackupName(itemName) {
    this.setState({
      backupName: itemName + " - " + this.formatDate(new Date()),
    });
  }

  formatDate(date) {
    // Define an array of month abbreviations
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
  
    // Extract hours, minutes, and seconds, ensuring two digits with padStart()
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
  
    // Extract day (two digits) and get month name from the array
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
  
    return `${hh}:${mm}:${ss} ${day} ${month} ${year}`;
  }

  render() {
    return (
      <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.container}>
        <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW}>
          <Heading className={classes.sectionHeading} type={Heading.types.H2}>New Snapshot</Heading>
        </Flex>
        <div className={classes.formTitle}>Snapshot type</div>
        <Dropdown 
          className={classes.fullWidth}
          placeholder="What type of backup do you want to perform?"
          onChange={(e) => { 
            this.setState({
              boardGroupName: null,
              boardGroupId: null, 
              itemName: null,
              itemId: null, 
              backupType: e ? e.value : null,
              isItemsDropdownsLoading: true,
              isBoardGroupDropdownsLoading: true
            }); 

            if((e.value === "board") || (e.value === "group")) {
              this.fetchItems("");
            } else if(e.value === "doc") {
              this.fetchDocs("");
            }
          }}
          options={[
            {
              label: 'Board',
              value: 'board'
            },
            {
              label: 'Board Group',
              value: 'group'
            },
            {
              label: 'Doc',
              value: 'doc'
            }
          ]} 
        />
        <div className={classes.formTitle}>Select an object to backup</div>
        <Dropdown 
          className={classes.fullWidth}
          placeholder="Select an item"
          isLoading={this.state.isItemsDropdownsLoading}
          value={ {label: this.state.itemName, value: this.state.itemId} }
          onInputChange={(newValue) => {
            this.setState({
              isItemsDropdownsLoading: true
            });
            if((this.state.backupType === "board") || (this.state.backupType === "group")) {
              this.fetchItems(newValue);
            } else if(this.state.backupType === "doc") {
              this.fetchDocs(newValue);
            }
          }}
          onChange={(e) => { 
            this.setState({ 
              boardGroupName: null,
              boardGroupId: null, 
              itemName: e ? e.label : null,
              itemId: e ? e.value : null,
              itemKind: e.board_kind,
              backupVisibility: (e.board_kind === "private") ? "private" : this.state.backupVisibility
            }); 

            if(this.state.backupType === "group") {
              this.fetchGroups(e.value)
            } else {
              this.generateBackupName(e.label);
            }
          }}
          options={this.state.itemOptions} 
        />
        {(this.state.backupType === "group") 
          ? <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.container}>
            <div className={classes.formTitle}>Select a board group</div>
            <Dropdown 
              className={classes.fullWidth}
              placeholder="Select a board group"
              isLoading={this.state.isBoardGroupDropdownsLoading}
              value={ {label: this.state.boardGroupName, value: this.state.boardGroupId} }
              onChange={(e) => { 
                this.setState({ 
                  boardGroupId: e ? e.value : null,
                  boardGroupName: e ? e.label : null,
                }); 

                this.generateBackupName(e.label);
              }}
              options={this.state.boardGroupOptions} 
            />
          </Flex>
          : <div></div>
        }
        <div className={classes.formTitle}>Snapshot visibility</div>
        <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} className={classes.fullWidth}>
            <RadioButton 
              text="Private" 
              name="visibility-group" 
              defaultChecked={this.state.backupVisibility === "private"} 
              checked={this.state.backupVisibility === "private"} 
              onSelect={() => { this.setState({backupVisibility: "private"}) 
              }} />
            <RadioButton 
              text="Public" 
              name="visibility-group" 
              disabled={this.state.itemKind === "private"}
              defaultChecked={this.state.backupVisibility === "public"} 
              checked={this.state.backupVisibility === "public"} 
              onSelect={() => { this.setState({backupVisibility: "public"}) 
              }} />
        </Flex>
        <div className={classes.formTitle}>Snapshot name</div>
        <TextField 
          size={TextField.sizes.MEDIUM} 
          value={this.state.backupName} 
          maxLength={200}
          onChange={(value) => { 
            this.setState({ 
              backupName: value
            }); 
          }}
          validation={this.validateBackupName(this.state.backupName)} 
        />
        <div className={classes.formTitle}>Description</div>
        <TextField 
          size={TextField.sizes.MEDIUM} 
          value={this.state.backupDescription} 
          maxLength={500}
          onChange={(value) => { 
            this.setState({ 
              backupDescription: value
            }); 
          }}
        />
        <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW}>
          <Button 
            size={Button.sizes.SMALL} 
            kind={Button.kinds.PRIMARY}
            loading={this.state.loadingOnCreation}
            disabled={this.state.loadingOnCreation}
            onClick={() => {
              this.save();
            }}
          >
            Create Snapshot
          </Button>
          <Button 
            size={Button.sizes.SMALL} 
            kind={Button.kinds.SECONDARY}
            disabled={this.state.loadingOnCreation}
            onClick={() => {
              this.state.onCloseClick();
            }}
          >
            Cancel
          </Button>
        </Flex>
      </Flex>
    );
  }
}

export default BackupEditor;
